import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { ApolloProvider } from '@apollo/client';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';

import App from './App';
import { store } from './store';
import theme from './theme';
import reportWebVitals from './reportWebVitals';
import { configService } from './services/configService';
import { applyEnvironmentDocumentTitle } from './utils/environment';
import { getApolloClient } from './apollo/client';

import './index.css';

// ローディング表示コンポーネント
const LoadingComponent = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress size={60} />
    <Typography variant="h6" sx={{ mt: 2 }}>
      アプリケーションを読み込んでいます...
    </Typography>
  </Box>
);

// エラー表示コンポーネント
const ErrorComponent = ({ message }: { message: string }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      p: 3,
    }}
  >
    <Typography variant="h4" color="error" gutterBottom>
      エラーが発生しました
    </Typography>
    <Typography variant="body1" align="center">
      {message}
    </Typography>
    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
      ページを再読み込みするか、しばらく経ってからもう一度お試しください。
    </Typography>
  </Box>
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// 初期表示としてローディング画面を表示
root.render(<LoadingComponent />);

// 設定を読み込んでからアプリケーションを起動
configService.loadConfig()
  .then((appConfig) => {
    applyEnvironmentDocumentTitle(appConfig.stage);

    // Apollo Clientのインスタンスを取得
    const apolloClient = getApolloClient();
    
    root.render(
      <React.StrictMode>
        <Provider store={store}>
          <ApolloProvider client={apolloClient}>
            <BrowserRouter>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <App />
              </ThemeProvider>
            </BrowserRouter>
          </ApolloProvider>
        </Provider>
      </React.StrictMode>
    );
  })
  .catch(error => {
    console.error('設定ファイルの読み込みに失敗しました:', error);
    root.render(
      <ErrorComponent message="アプリケーション設定の読み込みに失敗しました。" />
    );
  });

// パフォーマンス測定が必要な場合はコメントを解除してください
// reportWebVitals(console.log);
reportWebVitals();
